import React, {Fragment, useCallback, useState} from 'react';
import {
  Avatar,
  StyledVideoTile,
  Video,
  VideoTileStats,
  useBorderAudioLevel,
} from '@100mslive/roomkit-react';
import {
  useHMSStore,
  selectIsPeerAudioEnabled,
  selectPeerMetadata,
  selectLocalPeerID,
  selectPeerNameByID,
  selectAudioTrackByPeerID,
  selectVideoTrackByID,
  selectVideoTrackByPeerID,
} from '@100mslive/react-sdk';
import {MicOffIcon, HandRaiseFilledIcon, BrbIcon} from '@100mslive/react-icons';
import TileMenu from './TileMenu';
import {getVideoTileLabel} from './peerTileUtils';

import {UI_SETTINGS} from '../common/constants';
import {useIsHeadless, useUISettings} from '../AppData/useUISettings';
import {useAppConfig} from '../AppData/useAppConfig';
import {ConnectionIndicator} from '../Connection/ConnectionIndicator';
import {decryptText} from '../../utils/CommonFunction';

const Tile = ({peerId, trackId, showStatsOnTiles, width, height}: any) => {
  const trackSelector = trackId
    ? selectVideoTrackByID(trackId)
    : selectVideoTrackByPeerID(peerId);
  const track = useHMSStore(trackSelector);
  const peerName = decryptText(useHMSStore(selectPeerNameByID(peerId)));
  const audioTrack: any = useHMSStore(selectAudioTrackByPeerID(peerId));
  const localPeerID = useHMSStore(selectLocalPeerID);
  const isAudioOnly = useUISettings(UI_SETTINGS.isAudioOnly);
  const isHeadless = useIsHeadless();
  const isAudioMuted = !useHMSStore(selectIsPeerAudioEnabled(peerId));
  const isVideoMuted = !track?.enabled;
  const [isMouseHovered, setIsMouseHovered] = useState(false);
  const borderAudioRef = useBorderAudioLevel(audioTrack?.id);
  const isVideoDegraded = track?.degraded;
  const isLocal = localPeerID === peerId;
  const label = getVideoTileLabel({
    peerName,
    track,
    isLocal,
  });
  const onHoverHandler = useCallback((event: any) => {
    setIsMouseHovered(event.type === 'mouseenter');
  }, []);
  const appConfig = useAppConfig();

  return (
    <StyledVideoTile.Root
      css={{ width, height, padding: getPadding({ isHeadless, appConfig }) }}
      data-testid={`participant_tile_${peerName}`}
    >
      {peerName !== undefined ? (
        <StyledVideoTile.Container
          onMouseEnter={onHoverHandler}
          onMouseLeave={onHoverHandler}
          ref={
            appConfig?.headlessConfig?.hideAudioLevel
              ? undefined
              : borderAudioRef
          }
        >
          <ConnectionIndicator isTile peerId={peerId} />
          {showStatsOnTiles ? (
            <VideoTileStats
              audioTrackID={audioTrack?.id}
              videoTrackID={track?.id}
              peerID={peerId}
              isLocal={isLocal}
            />
          ) : null}

          {track ? (
            <Video
              trackId={track?.id}
              attach={isLocal ? undefined : !isAudioOnly}
              mirror={peerId === localPeerID && track?.source === "regular"}
              degraded={isVideoDegraded}
              data-testid="participant_video_tile"
            />
          ) : null}
          {isVideoMuted || isVideoDegraded || isAudioOnly ? (
            <StyledVideoTile.AvatarContainer>
              <Avatar
                name={peerName || ""}
                data-testid="participant_avatar_icon"
              />
            </StyledVideoTile.AvatarContainer>
          ) : null}
          {(!isHeadless ||
            (isHeadless && !appConfig?.headlessConfig?.hideTileName)) && (
            <StyledVideoTile.Info data-testid="participant_name_onTile">
              <span style={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                padding: 4
              }}>
                {label}
              </span>
            </StyledVideoTile.Info>
          )}
          {showAudioMuted({ appConfig, isHeadless, isAudioMuted }) ? (
            <StyledVideoTile.AudioIndicator
              style={{ background: "transparent" }}
              data-testid="participant_audio_mute_icon"
            >
              <img
                src={require("../../asset/icons/audioOff.png")}
                alt="logo"
                height={"24px"}
                width="24px"
              />
            </StyledVideoTile.AudioIndicator>
          ) : null}
          {isMouseHovered && !isHeadless && !isLocal ? (
            <TileMenu
              peerID={peerId}
              audioTrackID={audioTrack?.id}
              videoTrackID={track?.id}
            />
          ) : null}
          <PeerMetadata peerId={peerId} />
        </StyledVideoTile.Container>
      ) : null}
    </StyledVideoTile.Root>
  );
};

const metaStyles = {left: '20px', bottom: '20px'};

const PeerMetadata = ({peerId}: any) => {
  const metaData = useHMSStore(selectPeerMetadata(peerId));
  const isHandRaised = metaData?.isHandRaised || false;
  const isBRB = metaData?.isBRBOn || false;

  return (
    <Fragment>
      {isHandRaised ? (
        <StyledVideoTile.AttributeBox
          css={metaStyles}
          data-testid="raiseHand_icon_onTile">
          <div style={{backgroundImage:'linear-gradient(255.9deg, #1E9DAE -23.69%, #D478FF 116.28%)',height:36,width:36,borderRadius:100,cursor:'pointer',marginLeft:4}}>
          <img
            src={require('../../asset/icons/HandRaiseIcon.png')}
            alt='logo'
            width='20px'
            style={{marginTop:4,marginLeft:4}}
          />
        </div>
        </StyledVideoTile.AttributeBox>
      ) : null}
      {isBRB ? (
        <StyledVideoTile.AttributeBox
          css={metaStyles}
          data-testid="brb_icon_onTile">
          <BrbIcon width={40} height={40} />
        </StyledVideoTile.AttributeBox>
      ) : null}
    </Fragment>
  );
};

const VideoTile = React.memo(Tile);

const showAudioMuted = ({appConfig, isHeadless, isAudioMuted}: any) => {
  if (!isHeadless) {
    return isAudioMuted;
  }
  const hide = appConfig?.headlessConfig?.hideTileAudioMute;
  return isAudioMuted && !hide;
};

const getPadding = ({isHeadless, appConfig}: any) => {
  const offset = appConfig?.headlessConfig?.tileOffset;
  if (!isHeadless || typeof offset !== 'number') {
    return undefined;
  }
  return offset === 0 ? 0 : undefined;
};

export default VideoTile;
